import { Injectable } from '@angular/core';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
  RoleNameEnum,
} from '@g99-product-web-ui/shared-util-lib';

import { HttpService } from '../../core/http-service/http.service';
import { LocalStorageService } from '../helper-service/localStorage.service';
import { SubjectManageService } from '../helper-service/subject-manager.service';

@Injectable({
  providedIn: 'root',
})
export class SharedUserService {
  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _httpService: HttpService
  ) {}
  /**
   * Updates the current user data stored in local storage with the new values from the provided `userData` object.
   *
   * This method reads the current user data from local storage, checks if the data exists, and then updates the user's
   * first name, last name, email (as username), phone number, and designation based on the provided `userData`.
   * It also triggers a subject (`_userSubject`) with the updated user data to notify subscribers of the change.
   *
   * Note: The user roles and storage update in local storage are currently commented out.
   *
   * @param {IAddUserForm} userData - An object containing the updated user information to be applied to the current user.
   * @returns {void}
   */
  public updateUserValue(userData: IAuthResponse): void {
    if (userData) {
      this._localStorageService.storeItem('currentUser', userData);
      this._subjectManagerService.userSubject.next(userData);
    }
  }

  /**
   * Checks if the profile being edited belongs to the currently logged-in user.
   *
   * This method compares the `userId` parameter with the ID of the currently logged-in user
   * stored in local storage. If they match, it returns true, indicating that the profile being
   * edited is for the logged-in user.
   *
   * @param {number} userId - The ID of the user whose profile is being edited.
   * @returns {boolean} - Returns true if the profile belongs to the logged-in user, otherwise false.
   */
  public isProfileEditingForLoggedInUser(userId: number): boolean {
    const currentUser: IAuthResponse | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER);

    return currentUser?.id.toString() === userId.toString();
  }

  /**
   * Retrieves the currently logged-in user from local storage.
   *
   * This method reads the `currentUser` item from local storage and returns it.
   * If no user is found, it returns an empty object of type `IAuthResponse`.
   *
   * @returns {IAuthResponse} - The currently logged-in user or an empty object if not found.
   */
  public getLoggedInUser(): IAuthResponse {
    return (
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER) ||
      ({} as IAuthResponse)
    );
  }

  /**
   * Retrieves the ID of the currently logged-in user.
   *
   * This method reads the current user from local storage and returns their ID.
   * If no user is found, it returns 0.
   *
   * @returns {number} - The ID of the currently logged-in user or 0 if not found.
   */
  public getCurrentUserId(): number {
    const currentUser: IAuthResponse | null = this.getLoggedInUser();
    return currentUser?.id || 0;
  }

  /**
   * Checks if the current user is a support user.
   *
   * A support user is defined as a user whose `supportUser` property in the
   * `currentUser` object is truthy (not null, undefined, or 'false').
   *
   * @returns {boolean} - Returns true if the current user is a support user, otherwise false.
   */
  public isSupportUser(): boolean {
    const currentUser: IAuthResponse | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER);

    return !!(
      currentUser &&
      currentUser.supportUser &&
      currentUser.supportUser !== 'false'
    );
  }

  /**
   * Fetches user data from the API by user ID.
   *
   * This method constructs the API endpoint URL using the provided user ID and
   * retrieves user data from the server using the HttpService's get method.
   *
   * @param {number} userId - The ID of the user whose data is to be fetched.
   * @returns {Observable<any>} - An observable containing the user data.
   */

  public getUserData(userId: number) {
    const apiUrl = `${API_ENDPOINTS.USER_ENDPOINTS.v1User}${userId}`;
    return this._httpService.get<any>(apiUrl);
  }
  /**
   * Checks if the currently logged-in user has a staff role.
   *
   * This method retrieves the current user from local storage and checks if their
   * updated role is 'STAFF'. It returns true if the user is a staff member, otherwise false.
   *
   * @returns {boolean} - Returns true if the logged-in user is a staff member, otherwise false.
   */
  public isLoggedUserIsStaffUser(): boolean {
    const currentUser: IAuthResponse | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER) ||
      ({} as IAuthResponse);
    return currentUser.updatedRole?.name === RoleNameEnum.STAFF;
  }

  public isAuthenticated(): boolean {
    const user = this.getLoggedInUser();
    return !!user && !!user.id && !!user.idToken;
  }

  /**
   * Checks if the currently logged-in user is a super admin (G_SUPER_ADMIN),
   * regardless of any impersonated/updated role.
   *
   * @returns {boolean} - Returns true if the user has the super admin role.
   */
  public isSuperAdmin(): boolean {
    const currentUser: IAuthResponse | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
    if (!currentUser) {
      return false;
    }

    // Match exact role elements to avoid substring collisions
    // (e.g. a hypothetical "G_SUPER_ADMIN_LITE" must not match).
    const roles = Array.isArray(currentUser.roles)
      ? (currentUser.roles as string[])
      : String(currentUser.roles ?? '').split(',');

    return roles.map((role) => role.trim()).includes(RoleNameEnum.SUPER_ADMIN);
  }

  /**
   * Checks if the currently logged-in user is accessing via an admin/support
   * portal login — i.e. support-user flag, super admin, or a G_SUPER_ADMIN /
   * G_SUPPORT_USER role reported through `updatedRole`, `supportUserRoles`,
   * or `roles`.
   *
   * @returns {boolean} - Returns true if the session is an admin/support login.
   */
  public isSupportOrAdmin(): boolean {
    const currentUser = this.getLoggedInUser();
    const candidateValues = [
      currentUser?.updatedRole?.name,
      currentUser?.supportUserRoles,
      currentUser?.roles,
    ];

    // Match exact role tokens to avoid substring collisions
    // (e.g. a hypothetical "G_SUPER_ADMIN_LITE" must not match).
    const tokens = candidateValues
      .flatMap((value) =>
        Array.isArray(value) ? value : String(value ?? '').split(',')
      )
      .map((token) => token.trim());

    return (
      this.isSupportUser() ||
      this.isSuperAdmin() ||
      tokens.includes(RoleNameEnum.SUPER_ADMIN) ||
      tokens.includes('G_SUPPORT_USER')
    );
  }

  public hasAdminAccess(): boolean {
    const currentUser: IAuthResponse | null =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
    if (!currentUser) {
      return false;
    }

    // Check updatedRole first (most specific), then fall back to roles string
    const updatedRoleName = currentUser.updatedRole?.name ?? '';
    if (updatedRoleName === RoleNameEnum.ADMIN) {
      return true;
    }

    const rolesStr = Array.isArray(currentUser.roles)
      ? (currentUser.roles as string[]).join(',')
      : String(currentUser.roles ?? '');

    return (
      rolesStr.includes('G_SUPER_ADMIN') ||
      rolesStr.includes(RoleNameEnum.ADMIN)
    );
  }
}
